import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  CalendarClock,
  CheckCircle,
  AlertCircle,
  Plus,
  Trash2,
  Loader2,
  CreditCard,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Progress } from '@/components/ui/progress';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { useConfirmationDialog } from '@/components/ui/confirmation-dialog';

type Installment = {
  id: string;
  installmentNumber: number;
  description: string;
  dueDate: string;
  amount: number;
  paidAmount: number;
  status: 'PENDING' | 'PARTIALLY_PAID' | 'PAID' | 'OVERDUE';
};

type PaymentPlan = {
  id: string;
  totalAmount: number;
  currency: string;
  status: 'ACTIVE' | 'COMPLETED' | 'CANCELLED';
  template?: { name: string } | null;
  installments: Installment[];
};

interface PaymentPlanCardProps {
  bookingId: string;
  totalAmount: number;
  currency: string;
  travelStartDate: string;
  paymentPlan: PaymentPlan | null;
  onSuccess: () => void;
  onRecordPayment: (installmentId: string, amount: number) => void;
}

export function PaymentPlanCard({
  bookingId,
  totalAmount,
  currency,
  travelStartDate,
  paymentPlan,
  onSuccess,
  onRecordPayment,
}: PaymentPlanCardProps) {
  const { t } = useTranslation('paymentPlans');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const deleteDialog = useConfirmationDialog();

  const { data: templates } = trpc.paymentPlanTemplate.list.useQuery({
    activeOnly: true,
  });

  const { data: preview, isLoading: previewLoading } = trpc.paymentPlanTemplate.preview.useQuery(
    {
      templateId: selectedTemplateId,
      totalAmount,
      travelStartDate: new Date(travelStartDate),
    },
    {
      enabled: !!selectedTemplateId && showPreview,
    }
  );

  const utils = trpc.useUtils();

  const createFromTemplateMutation = trpc.paymentPlan.createFromTemplate.useMutation({
    onSuccess: () => {
      utils.booking.byId.invalidate(bookingId);
      showSuccessToast(t('plan.successCreated'));
      setSelectedTemplateId('');
      setShowPreview(false);
      setIsCreating(false);
      onSuccess();
    },
    onError: (error) => {
      showErrorToast(error.message || t('plan.failedCreate'));
    },
  });

  const deletePlanMutation = trpc.paymentPlan.delete.useMutation({
    onSuccess: () => {
      utils.booking.byId.invalidate(bookingId);
      showSuccessToast(t('plan.successDeleted'));
      onSuccess();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const handleApplyTemplate = () => {
    if (!selectedTemplateId) return;
    createFromTemplateMutation.mutate({
      bookingId,
      templateId: selectedTemplateId,
    });
  };

  const handleDeletePlan = () => {
    if (!paymentPlan) return;

    deleteDialog.confirm(
      {
        title: t('plan.deletePlan'),
        description: t('plan.confirmDelete'),
        confirmLabel: t('plan.deletePlan'),
        variant: 'destructive',
      },
      () => {
        deletePlanMutation.mutate(paymentPlan.id);
      }
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'bg-green-100 text-green-700';
      case 'PARTIALLY_PAID':
        return 'bg-orange-100 text-orange-700';
      case 'OVERDUE':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getDaysUntilDue = (dueDate: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // If there's no payment plan, show the create option
  if (!paymentPlan) {
    return (
      <div className="rounded-xl border bg-card p-6">
        <h2 className="font-semibold flex items-center gap-2 mb-4">
          <CalendarClock className="h-5 w-5" />
          {t('plan.title')}
        </h2>

        {!isCreating ? (
          <div className="text-center py-4">
            <p className="text-muted-foreground text-sm mb-4">{t('plan.noPlan')}</p>
            <Button onClick={() => setIsCreating(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('plan.createPlan')}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('plan.selectTemplate')}</label>
              <Combobox
                options={templates?.map((template) => ({
                  value: template.id,
                  label: `${template.name}${template.isDefault ? ' ★' : ''}`,
                })) ?? []}
                value={selectedTemplateId}
                onValueChange={(value) => {
                  setSelectedTemplateId(value);
                  setShowPreview(true);
                }}
                placeholder={t('plan.selectTemplate')}
                searchPlaceholder="Search templates..."
              />
            </div>

            {/* Preview */}
            {showPreview && selectedTemplateId && (
              <div className="rounded-lg border p-4 bg-muted/50">
                <h4 className="font-medium text-sm mb-3">{t('plan.preview')}</h4>
                {previewLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : preview ? (
                  <div className="space-y-2">
                    {preview.installments.map((inst, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <div>
                          <span className="text-muted-foreground">{inst.description}</span>
                          <span className="text-xs text-muted-foreground ml-2">
                            ({new Date(inst.dueDate).toLocaleDateString()})
                          </span>
                        </div>
                        <span className="font-medium">
                          {formatCurrency(inst.amount, currency)}
                        </span>
                      </div>
                    ))}
                    <div className="border-t pt-2 mt-2 flex justify-between font-medium">
                      <span>{t('plan.totalAmount')}</span>
                      <span>{formatCurrency(totalAmount, currency)}</span>
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreating(false);
                  setSelectedTemplateId('');
                  setShowPreview(false);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleApplyTemplate}
                disabled={!selectedTemplateId || createFromTemplateMutation.isPending}
              >
                {createFromTemplateMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t('plan.apply')}
              </Button>
            </div>
          </div>
        )}

        {deleteDialog.Dialog}
      </div>
    );
  }

  // Show existing payment plan
  const paidAmount = paymentPlan.installments.reduce(
    (sum, inst) => sum + inst.paidAmount,
    0
  );
  const progressPercent = paymentPlan.totalAmount > 0
    ? (paidAmount / paymentPlan.totalAmount) * 100
    : 0;

  return (
    <div className="rounded-xl border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold flex items-center gap-2">
          <CalendarClock className="h-5 w-5" />
          {t('plan.title')}
        </h2>
        <div className="flex items-center gap-2">
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
              paymentPlan.status === 'COMPLETED'
                ? 'bg-green-100 text-green-700'
                : paymentPlan.status === 'CANCELLED'
                ? 'bg-red-100 text-red-700'
                : 'bg-blue-100 text-blue-700'
            }`}
          >
            {t(`plan.${paymentPlan.status.toLowerCase()}`)}
          </span>
          {paymentPlan.status === 'ACTIVE' && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive"
              onClick={handleDeletePlan}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {paymentPlan.template && (
        <p className="text-sm text-muted-foreground mb-4">
          Template: {paymentPlan.template.name}
        </p>
      )}

      {/* Progress */}
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-muted-foreground">{t('plan.paidAmount')}</span>
          <span className="font-medium">
            {formatCurrency(paidAmount, paymentPlan.currency)} / {formatCurrency(paymentPlan.totalAmount, paymentPlan.currency)}
          </span>
        </div>
        <Progress value={progressPercent} className="h-2" />
      </div>

      {/* Installments */}
      <div className="space-y-3">
        {paymentPlan.installments.map((installment) => {
          const daysUntil = getDaysUntilDue(installment.dueDate);
          const isPastDue = daysUntil < 0 && installment.status !== 'PAID';
          const remainingAmount = installment.amount - installment.paidAmount;

          return (
            <div
              key={installment.id}
              className={`rounded-lg border p-3 ${
                isPastDue ? 'border-red-200 bg-red-50' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {installment.status === 'PAID' ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : isPastDue ? (
                      <AlertCircle className="h-4 w-4 text-red-600" />
                    ) : (
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="font-medium text-sm">
                      {installment.description}
                    </span>
                    <span className={`rounded px-1.5 py-0.5 text-xs ${getStatusColor(installment.status)}`}>
                      {t(`installment.${installment.status.toLowerCase()}`)}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground ml-6">
                    <p>
                      Due: {new Date(installment.dueDate).toLocaleDateString()}
                      {daysUntil > 0 && installment.status !== 'PAID' && (
                        <span className="ml-2 text-xs">
                          ({t('installment.dueIn', { days: daysUntil })})
                        </span>
                      )}
                      {daysUntil === 0 && installment.status !== 'PAID' && (
                        <span className="ml-2 text-xs text-orange-600">
                          ({t('installment.dueToday')})
                        </span>
                      )}
                      {isPastDue && (
                        <span className="ml-2 text-xs text-red-600">
                          ({t('installment.daysOverdue', { days: Math.abs(daysUntil) })})
                        </span>
                      )}
                    </p>
                    <p className="font-medium">
                      {formatCurrency(installment.amount, paymentPlan.currency)}
                      {installment.paidAmount > 0 && installment.status !== 'PAID' && (
                        <span className="text-green-600 ml-2">
                          ({formatCurrency(installment.paidAmount, paymentPlan.currency)} paid)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                {remainingAmount > 0 && paymentPlan.status === 'ACTIVE' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onRecordPayment(installment.id, remainingAmount)}
                  >
                    <CreditCard className="mr-1 h-3 w-3" />
                    {t('installment.payNow')}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {deleteDialog.Dialog}
    </div>
  );
}
